import type { GameRoomState } from "@presidents/game-engine";
// eslint-disable-next-line no-duplicate-imports
import { GameState } from "@presidents/game-engine";
import type { RuleOptions } from "@presidents/game-engine/src/rules";
import { dequal } from "dequal";
import React, { useEffect, useReducer, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useSocket } from "../context/SocketContext";
import { useUser } from "../context/UserContext";
import useLiveKit from "../hooks/webrtc/useLiveKit";
// import useMeshWebRTC from "../hooks/useMeshWebRTC";
import arrangePlayers from "../utils/arrange-players";
import { Confetti } from "./animation/Confetti";
import { Button } from "./Button";
import CardsInPlay from "./CardsInPlay";
import Dialog from "./Dialog";
import GameMessages from "./GameMessages";
import GameRules, { hasEnabledRules } from "./GameRules";
import { GlobalControls } from "./GlobalControls";
import { HeadingSmall } from "./Heading";
import Cog from "./icons/Cog";
import LeaderBoard from "./LeaderBoard";
import MyHandAndControls, {
  Controls
} from "./my-hand-and-controls/MyHandAndControls";
import PlayerRanks from "./PlayerRanks";
import PlayerSection from "./PlayerSection";
import Grid from "./VideoGrid";

export enum GameDialogs {
  ranks = "ranks",
  leaderboard = "leaderboard",
  rules = "rules"
}

const fadeInKeyframes = keyframes`
  from {
    opacity: 0
  }
`;

const Element = styled.section`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: var(--bottom-control-bar-height);
  max-width: 900px;
  margin: auto;
`;
const CenterElement = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const GameDialog = styled.div`
  padding: var(--spacing-large);
  animation: ${fadeInKeyframes} 250ms ease-out 1;
  text-align: center;
`;

const ActionButton = styled.a`
  position: fixed;
  top: 8px;
  right: 8px;
  width: 48px;
  height: 48px;
  background-color: var(--foreground);
  border-radius: 50%;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 48px;
  font-size: 24px;
`;

type Props = {
  gameRoomState: GameRoomState;
};

type LocalState = {
  dialogStack: GameDialogs[];
};

const defaultState: LocalState = {
  dialogStack: []
};

type Action = {
  type: "GAME_START" | "GAME_END" | "NEXT" | "PRE";
};
type ActionWithState = {
  type: "SET_STATE";
  state: Partial<LocalState>;
};

const Game: React.FC<Props> = ({ gameRoomState }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [gameLoading, setGameLoading] = useState(false);
  const {
    options: gameRoomOptions,
    numGames,
    players,
    playerRanks,
    roomId,
    leaderBoard,
    currentGame,
    selected: playerSelected = {}
  } = gameRoomState;

  const gameState = currentGame?.state;
  const gameOptions = currentGame?.options;

  const user = useUser();

  const isFinished = gameState?.state === "FINISH";
  const shouldRedeal =
    players.length > 1 &&
    gameState?.state === GameState.START_ROUND &&
    !gameState.whosNext;
  const numFinishedGames = isFinished ? numGames : numGames - 1;
  const gameLimitReached = numGames >= gameRoomOptions.gameLimit;
  const canNewGame =
    !gameLimitReached && players.length > 1 && (!gameState || isFinished);
  const canStartGame = players.length > 1 && gameState?.state === "DEAL";
  const showTurns = !!gameState?.turns.length;
  const shouldSwap = !!gameOptions?.rules?.shouldSwap;

  const [{ dialogStack }, dispatch] = useReducer(
    (s: LocalState, action: Action | ActionWithState): LocalState => {
      let nextState = { ...s };
      const currDialogs = nextState.dialogStack;
      switch (action.type) {
        case "GAME_START":
          {
            if (!currDialogs.includes(GameDialogs.rules)) {
              nextState.dialogStack = [...currDialogs, GameDialogs.rules];
            }
          }
          break;
        case "GAME_END":
          {
            nextState = {
              dialogStack: [GameDialogs.ranks, GameDialogs.leaderboard]
            };
          }
          break;
        case "NEXT":
          {
            if (currDialogs.length > 1) {
              const nextDialogStack = currDialogs.slice(1);
              nextState.dialogStack = nextDialogStack;
            } else {
              nextState.dialogStack = [];
            }
          }
          break;
        case "SET_STATE":
          {
            nextState = {
              ...s,
              ...action.state
            };
          }
          break;
        default:
          nextState = {
            ...defaultState
          };
      }
      return nextState;
    },
    defaultState
  );

  const [everyoneMuted, setEveryoneMuted] = useState(false);
  const [, setShowStats] = useState(false);

  const socket = useSocket((s) => {
    const handleError = () => {
      setGameLoading(false);
    };
    s.on("error", handleError);
    return () => {
      s.off("error", handleError);
    };
  });

  const { room, participants } = useLiveKit(roomId); // useSfuWebRTC(roomId, players); // useMeshWebRTC(roomId, players);

  const lastStateRef = useRef<string>(gameState?.state ?? "unknown");
  useEffect(() => {
    if (lastStateRef.current !== gameState?.state) {
      lastStateRef.current = gameState?.state ?? "unknown";
      setGameLoading(false);
      if (gameState?.state === "FINISH") {
        // Game has just finished!
        setTimeout(() => {
          dispatch({
            type: "GAME_END"
          });
        }, 2000);
      } else if (gameState?.state === "START_ROUND" && gameState.round === 0) {
        setTimeout(() => {
          dispatch({
            type: "GAME_START"
          });
        }, 2000);
      }
    }
  }, [gameState?.state]);

  const lastRules = useRef<Partial<RuleOptions> | undefined>(
    currentGame?.options.rules
  );
  useEffect(() => {
    if (!lastRules.current && currentGame?.options) {
      lastRules.current = currentGame?.options.rules;
    } else if (currentGame?.options.rules) {
      const hasChanged = !dequal(lastRules.current, currentGame.options.rules);
      if (hasChanged) {
        lastRules.current = currentGame?.options.rules;
      }
    }
    if (typeof window !== "undefined") {
      (window as any).dispatch = dispatch;
    }
  }, [gameState?._id]);

  const onClickResetGame = () => {
    socket?.emit("resetgame", { roomId });
    setShowMenu(false);
  };
  const onClickNewGame = () => {
    setGameLoading(true);
    socket?.emit("newgame", { roomId });
    setShowMenu(false);
  };
  const onClickStartGame = () => {
    setGameLoading(true);
    socket?.emit("startgame", { roomId });
  };
  const onClickQuitGame = () => {
    socket?.emit("quitgame", { roomId });
    window.location.href = "/";
  };

  const rotatedArr = arrangePlayers(user, players, playerRanks);

  const showControls = canNewGame || canStartGame || shouldRedeal;

  const currDialog: GameDialogs | null = dialogStack[0] || null;
  const showDialog =
    currDialog === GameDialogs.rules
      ? hasEnabledRules(gameRoomOptions.rules, numGames)
      : !!currDialog;
  const nextModal = dialogStack.length > 1;
  const showNextOrCloseDialog = () => {
    dispatch({ type: "NEXT" });
  };

  return (
    <Element>
      <Grid>
        {rotatedArr.map((player, i) => {
          const isMe = player.id === user?.uid;
          const participant = participants[player.id];
          return (
            <PlayerSection
              key={i}
              isPresident={playerRanks[0] === player.id}
              isAsshole={playerRanks[playerRanks.length - 1] === player.id}
              player={player}
              position={i}
              total={rotatedArr.length}
              isActive={gameState?.whosNext === player.id}
              selected={playerSelected[player.id]}
              stats={gameState?.handStats[player.id]}
              rank={gameState?.handStats[player.id]?.rank}
              // isMe={isMe}
              participant={participant}
            />
          );
        })}
      </Grid>
      <CenterElement>
        <CardsInPlay
          gameState={gameState}
          showCards={!showControls && showTurns}
        />
        <GameMessages gameRoomState={gameRoomState} />
      </CenterElement>
      <MyHandAndControls
        roomId={roomId}
        gameState={gameState}
        gameOptions={gameOptions}
      >
        {showControls && (
          <Controls>
            {canNewGame && (
              <Button onClick={onClickNewGame} disabled={gameLoading}>
                Deal
              </Button>
            )}
            {canStartGame && (
              <Button onClick={onClickStartGame} disabled={gameLoading}>
                {shouldSwap ? "Swap Cards" : "Start"}
              </Button>
            )}
            {shouldRedeal && (
              <Button onClick={onClickResetGame} disabled={gameLoading}>
                Redeal
              </Button>
            )}
          </Controls>
        )}
      </MyHandAndControls>
      <GlobalControls
        room={room}
        localParticipant={room?.localParticipant}
        gameRoomState={gameRoomState}
        showMenu={showMenu}
        requestNewGame={onClickNewGame}
        requestResetGame={onClickResetGame}
        requestQuitGame={onClickQuitGame}
        showLeaderBoard={() => {
          setShowMenu(false);
          dispatch({
            type: "SET_STATE",
            state: {
              dialogStack: [GameDialogs.leaderboard]
            }
          });
        }}
      ></GlobalControls>
      <ActionButton
        onClick={() => {
          setShowStats(false);
          setShowMenu(!showMenu);
        }}
      >
        {showMenu ? "✕" : <Cog width={24} height={24} />}
      </ActionButton>
      <Confetti />
      <Dialog
        isOpen={showDialog}
        onDismiss={showNextOrCloseDialog}
        appearance="centered"
      >
        <GameDialog>
          {currDialog === GameDialogs.ranks && (
            <>
              <HeadingSmall>Game over!</HeadingSmall>
              <PlayerRanks playerRanks={playerRanks} players={players} />
              <Button size="small" onClick={showNextOrCloseDialog}>
                {nextModal ? "Next" : "Close"}
              </Button>
            </>
          )}
          {currDialog === GameDialogs.leaderboard && (
            <>
              <HeadingSmall>
                After {numFinishedGames} game
                {numFinishedGames > 1 ? "s" : ""}
              </HeadingSmall>
              <LeaderBoard leaderBoard={leaderBoard} players={players} />
              <Button
                appearance="secondary"
                size="small"
                onClick={showNextOrCloseDialog}
              >
                {nextModal ? "Next" : "Close"}
              </Button>
            </>
          )}
          {hasEnabledRules(gameRoomOptions.rules, numGames) &&
            currDialog === GameDialogs.rules && (
              <>
                <HeadingSmall>
                  Game {gameRoomState.numGames} rules:
                </HeadingSmall>
                <GameRules numGames={numGames} rules={gameRoomOptions.rules} />
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={showNextOrCloseDialog}
                >
                  {nextModal ? "Next" : "Close"}
                </Button>
              </>
            )}
        </GameDialog>
      </Dialog>
    </Element>
  );
};

export default Game;
